import { useCallback } from 'react';
import { PageContainer } from '../components/layout';
import LodgeForm from '../components/lodge/LodgeForm';
import { useAuth } from '../auth/useAuth';
import { useActor } from '../hooks/useActor';
import { useActionRuntime } from '../hooks/useActionRuntime';
import { searchClients } from '../services/clientDirectory';
import styles from '../components/lodge/LodgeForm.module.css';

/**
 * Lodge a report (13-24): the office lodge for all five registers, rebuilt on the field set.
 *
 * The shell is gated to the office roles (App.tsx); the form offers only the registers the role may lodge
 * (`config/roles.ts` `canDo`), and the Writer runs the lodge gate again before anything is sent (D-13-23-3).
 * Writes go through the runtime's Writer, so practice mode (dev, and until 13-30's live flag) saves nothing
 * (D-13-L-9). The client picker reads the two client lists as the signed-in person, in memory only (D-13-L-7).
 */
export default function LodgePage() {
  const { roles } = useAuth();
  const { writer, io, siteError } = useActionRuntime();
  const actorState = useActor();
  const client = io?.client ?? null;

  const search = useCallback(
    (stream: string, query: string) =>
      client ? searchClients({ client }, stream, query) : Promise.reject(new Error('Not connected yet')),
    [client],
  );

  return (
    <PageContainer title="Lodge a report" maxWidth="md">
      {/* Who triages follows recordAuthority's byRegister rule; HR & WHS sees the HR/WHS-touching records (roles.ts). */}
      <p className={styles.next}>
        <strong>What happens next:</strong> an incident, complaint or feedback is triaged by a Care Manager or
        Oversight. A risk or improvement is triaged by Oversight. HR &amp; WHS sees and investigates the staff and WHS
        ones. For incidents, Oversight makes the reportable call (for aged care, the SaH reportability decider).
      </p>
      <LodgeForm
        actor={actorState.actor}
        roles={actorState.actor?.roles ?? roles}
        writer={writer}
        io={io}
        siteError={siteError}
        searchClients={search}
      />
    </PageContainer>
  );
}
